<script setup lang="ts">
/* =============================================================
   COMPANY — CREATE / EDIT
   =============================================================
   The sibling of BerthFormPage; the notes there about why this
   is a page rather than a dialog, and why create and edit share
   one component, apply here too.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUnsavedGuard } from '@/lib/useUnsavedGuard'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import TextField from '@/components/ui/TextField.vue'
import CountrySelect from '@/components/ui/CountrySelect.vue'
import Alert from '@/components/ui/Alert.vue'
import { routes, to } from '@/config/routes'
import { createCompany, getCompany, updateCompany } from '@/lib/companies'
import { DataError } from '@/lib/errors'
import type { CompanyInput } from '@/lib/types'

const route = useRoute()
const router = useRouter()

const editingId = computed(() => (route.params.id ? Number(route.params.id) : null))
const isEdit = computed(() => editingId.value !== null)

interface FormState {
  name: string
  country_code: string
  vat_number: string
  contact_email: string
  website: string
}

const blank: FormState = {
  name: '',
  country_code: '',
  vat_number: '',
  contact_email: '',
  website: '',
}

const form = ref<FormState>({ ...blank })
const pristine = ref<FormState>({ ...blank })
const heading = ref('New company')

const loading = ref(false)
const saving = ref(false)
const notFound = ref(false)
const formError = ref('')
const errors = ref<Record<string, string>>({})

const dirty = computed(() => JSON.stringify(form.value) !== JSON.stringify(pristine.value))

onMounted(async () => {
  if (!isEdit.value) return
  loading.value = true
  try {
    const company = await getCompany(editingId.value as number)
    if (!company) {
      notFound.value = true
      return
    }
    const state: FormState = {
      name: company.name,
      country_code: company.country_code,
      vat_number: company.vat_number ?? '',
      contact_email: company.contact_email,
      website: company.website ?? '',
    }
    form.value = { ...state }
    pristine.value = { ...state }
    heading.value = company.name
  } catch (e) {
    formError.value = e instanceof DataError ? e.message : 'Could not load the company.'
  } finally {
    loading.value = false
  }
})

/** Mirrors the CHECK constraints on public.companies. The database decides. */
function validate(): CompanyInput | null {
  const next: Record<string, string> = {}

  const name = form.value.name.trim()
  if (!name) next.name = 'Enter a name.'

  const country_code = form.value.country_code
  if (!country_code) next.country_code = 'Choose a country.'

  const contact_email = form.value.contact_email.trim()
  if (!contact_email) next.contact_email = 'Enter a contact email.'
  else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact_email))
    next.contact_email = 'Enter a valid email address.'

  // Only an obviously-wrong website is rejected here. clean() in lib/companies
  // adds the scheme when it is missing, so a bare domain is accepted on the
  // way in rather than bounced back as an error.
  const site = form.value.website.trim()
  if (site && /\s/.test(site)) next.website = 'A web address cannot contain spaces.'

  errors.value = next
  if (Object.keys(next).length > 0) return null

  const vat = form.value.vat_number.trim()
  return {
    name,
    country_code,
    contact_email,
    vat_number: vat ? vat : null,
    website: site ? site : null,
  }
}

async function onSubmit(): Promise<void> {
  if (saving.value) return
  const input = validate()
  if (!input) return

  saving.value = true
  formError.value = ''
  try {
    const saved = isEdit.value
      ? await updateCompany(editingId.value as number, input)
      : await createCompany(input)
    pristine.value = { ...form.value }
    await router.push(to.company(saved.id))
  } catch (e) {
    formError.value = e instanceof DataError ? e.message : 'Could not save the company.'
  } finally {
    saving.value = false
  }
}

const cancelTo = computed(() =>
  isEdit.value ? to.company(editingId.value as number) : routes.companies,
)

// Asks before leaving with unsaved edits -- see lib/useUnsavedGuard.ts.
useUnsavedGuard(dirty, saving)
</script>

<template>
  <PageHeader
    :title="isEdit ? `Edit ${heading}` : 'New company'"
    description="The organisation that owns berths and is the party to a booking."
  />

  <p v-if="loading" class="page-status">Loading…</p>

  <Alert v-else-if="notFound" tone="danger">
    That company does not exist.
    <RouterLink :to="routes.companies">Back to all companies</RouterLink>.
  </Alert>

  <Panel v-else>
    <form class="form-layout" novalidate @submit.prevent="onSubmit">
      <Alert v-if="formError" tone="danger">{{ formError }}</Alert>

      <div class="form-grid">
        <TextField
          v-model="form.name"
          label="Name"
          :error="errors.name"
          placeholder="Rijnhaven Beheer B.V."
          autocomplete="off"
          class="form-wide"
          required
        />
        <CountrySelect
          v-model="form.country_code"
          :error="errors.country_code"
          hint="Country of registration."
        />
        <TextField
          v-model="form.vat_number"
          label="VAT number"
          hint="Optional"
          :error="errors.vat_number"
          placeholder="NL001234567B01"
          autocomplete="off"
        />
        <TextField
          v-model="form.contact_email"
          label="Contact email"
          type="email"
          :error="errors.contact_email"
          hint="Shown on booking requests."
          placeholder="harbour@rijnhaven.nl"
          autocomplete="off"
          required
        />
        <TextField
          v-model="form.website"
          label="Website"
          :error="errors.website"
          hint="Optional. https:// is added if you leave it off."
          placeholder="rijnhaven.nl"
          autocomplete="off"
        />
      </div>

      <footer class="form-actions">
        <Button variant="ghost" :to="cancelTo" :disabled="saving">Cancel</Button>
        <Button type="submit" :loading="saving">
          {{ isEdit ? 'Save changes' : 'Create company' }}
        </Button>
      </footer>
    </form>
  </Panel>
</template>
